import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Tabs from "../../components/tab/component";
import AppTextField from "../../components/text-field";
import AppButton from "../../components/button/component";
import { userGuid } from "../../utils/general-config";
import Loader from "../../components/loader";
import ArrowIcon from "../../assets/settings/arrow-icon.svg";
import { toast } from "react-toastify";

function AdminReactivateUser({ UpdateUserConnect, bulkReactivateConnect }) {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get("tab") || "tab1";

  const tabData = [
    {
      id: "tab1",
      label: "Reactivate User",
      content: (
        <ReactivateUserAccount bulkReactivateConnect={bulkReactivateConnect} />
      ),
    },
    {
      id: "tab2",
      label: "Update Premium",
      content: <UpdateIsPremium UpdateUserConnect={UpdateUserConnect} />,
    },
    {
      id: "tab3",
      label: "Lock/UnLockAccount",
      content: <LockUnLockAccount UpdateUserConnect={UpdateUserConnect} />,
    },
  ];

  const validTabs = tabData.map((tab) => tab.id);
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(tabParam) ? tabParam : "tab1"
  );

  const handleTabChange = useCallback(
    (tabId) => {
      if (validTabs.includes(tabId)) {
        setActiveTab(tabId);
        searchParams.set("tab", tabId);
        navigate(`?${searchParams.toString()}`, { replace: true });
      }
    },
    [navigate, searchParams, validTabs]
  );

  useEffect(() => {
    const currentTab = searchParams.get("tab");
    if (
      currentTab &&
      validTabs.includes(currentTab) &&
      currentTab !== activeTab
    ) {
      setActiveTab(currentTab);
    }
  }, [location.search, activeTab, validTabs]);

  return (
    <div className="w-full flex justify-center items-center">
      <div className="container w-full max-w-screen-lg ">
        <Tabs
          tabs={tabData}
          initialTab={activeTab}
          onTabClick={handleTabChange}
          orientation={"vertical"}
        />
      </div>
    </div>
  );
}

export default AdminReactivateUser;

function UpdateIsPremium({ UpdateUserConnect }) {
  const dropdownRef = useRef(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [formData, setFormData] = useState({
    userId: "",
    status: "",
  });
  const [errors, setErrors] = useState({
    userId: "",
    status: "",
  });

  const STATUS_OPTION = [
    { label: "YES", value: "true" },
    { label: "NO", value: "false" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleToggleSearch = () => setIsSearchOpen((prev) => !prev);

  const handleSelectSearch = (option) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      status: option.label, // Display the label in the input
    }));
    setErrors((prevErrors) => ({ ...prevErrors, status: "" }));
    // Ensure dropdown closes
    setTimeout(() => setIsSearchOpen(false), 100);
  };

  const validateForm = () => {
    let isValid = true;
    let newErrors = { userId: "", status: "" };

    if (!formData.userId.trim()) {
      newErrors.userId = "User ID is required.";
      isValid = false;
    }

    if (!formData.status.trim()) {
      newErrors.status = "Status is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        userGuid: formData.userId,
        isPremium: formData.status === "YES" ? true : false,
      };
      UpdateUserConnect(payload)
        .then((response) => {
          if (!!response) {
            toast.success("Update isPremium successfully!!");
          }
        })
        .catch((err) => {
          toast.error("Update isPremium failed!! ");
        });
      // Perform submit logic here
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <h5 className="text-2xl font-semibold mb-4">Update User IsPremium</h5>

      {/* User ID Input */}
      <div className="relative w-full max-w-xs mb-4">
        <AppTextField
          name="userId"
          onChange={handleInputChange}
          value={formData.userId}
          placeholder="Enter User ID"
          className="mb-1"
        />
        {errors.userId && (
          <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
        )}
      </div>

      {/* Status Dropdown */}
      <div
        className="relative w-full max-w-xs mb-4"
        ref={dropdownRef}
        onClick={handleToggleSearch}
      >
        <input
          id="status"
          name="status"
          value={formData.status} // Display the selected label
          placeholder="Select status"
          className="px-3 py-[10px] block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm cursor-pointer"
          readOnly // Make input behave like a dropdown
          aria-expanded={isSearchOpen}
        />
        <span className="absolute right-3 top-[1.5rem] transform -translate-y-1/2">
          <img src={ArrowIcon} alt="Arrow Icon" className="w-4 h-4" />
        </span>
        {isSearchOpen && (
          <div className="absolute mt-1 rounded-lg bg-white shadow-lg z-10 w-full">
            <ul
              className="rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 max-h-40 overflow-auto focus:outline-none sm:text-sm"
              role="listbox"
            >
              {STATUS_OPTION.map((option) => (
                <li
                  key={option.value}
                  className="text-gray-900 cursor-pointer select-none py-2 px-4 hover:bg-gray-100 hover:text-black"
                  onClick={() => {
                    handleSelectSearch(option);
                    setIsSearchOpen(false);
                  }}
                  role="option"
                  aria-selected={formData.status === option.label}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        {errors.status && (
          <p className="text-red-500 text-sm mt-1">{errors.status}</p>
        )}
      </div>

      {/* Submit Button */}
      <div className="relative w-full max-w-xs mt-2">
        <AppButton
          type="submit"
          className="bg-[#FE6634] hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-white w-full py-2 rounded-md"
        >
          Submit
        </AppButton>
      </div>
    </form>
  );
}

function ReactivateUserAccount({ bulkReactivateConnect }) {
  const [formData, setFormData] = useState({
    subscriptionId: "",
    planId: "",
  });
  const [errors, setErrors] = useState({
    subscriptionId: "",
    planId: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [resData, setResData] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault(); // Prevent the default form submission
    const { subscriptionId, planId } = formData;

    // Basic validation
    if (!subscriptionId) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        subscriptionId: "Subscription ID is required.",
      }));
      return;
    }
    if (subscriptionId.split(",").map((id) => id.trim()).length > 50) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        subscriptionId: "Subscription ID must not exceed 50 characters.",
      }));
      return;
    }
    if (!planId) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        planId: "Plan ID is required.",
      }));
      return;
    }

    // Create the payload
    const payload = {
      subscriptionId: subscriptionId.split(",").map((id) => id.trim()), // Split and trim the subscription IDs
      planId: planId,
    };
    setIsLoading(true);

    bulkReactivateConnect(payload)
      .then((res) => {
        if (res) {
          setResData(res);
          const parts = res.split("|");
          // Trim the parts to remove any leading or trailing whitespace
          const beforePipe = parts[0].trim(); // This will be an empty string in this case
          const afterPipe = parts[1] ? parts[1].trim() : "";
          setSuccessMessage(beforePipe);
          setFailureMessage(afterPipe);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // Here you would typically send the payload to your API
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <div className="flex flex-col lg:flex-row justify-center items-start w-full gap-4">
        <form onSubmit={handleSubmit} className="w-full max-w-lg">
          <div className="relative w-full mb-4">
            <h5 className="text-xl font-semibold">
              Enter Subscription Id (Max 50)
            </h5>
            <textarea
              name="subscriptionId"
              rows="7"
              className="block w-full p-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter Subscription Id Comma Separated ..."
              value={formData.subscriptionId}
              onChange={handleInputChange}
            ></textarea>
            {errors.subscriptionId && (
              <p className="mt-1 text-sm text-red-500">
                {errors.subscriptionId}
              </p>
            )}
          </div>
          <div className="relative w-full mb-4">
            <AppTextField
              name="planId"
              onChange={handleInputChange}
              value={formData?.planId}
              placeholder="Enter Plan ID"
              className="mt-2"
            />
            {errors.planId && (
              <p className="mt-1 text-sm text-red-500">{errors.planId}</p>
            )}
          </div>
          <AppButton
            type="submit"
            className="bg-[#FE6634] hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-white w-full py-2 rounded-md"
          >
            Reactivate
          </AppButton>
        </form>
        {!!resData && (
          <div className="flex flex-col w-full mt-4">
            <div>
              <p>Success :</p>
              <div className="w-full max-w-screen-lg h-40 text-green-500 p-2 bg-gray-100 overflow-auto rounded-lg">
                {successMessage}
              </div>
            </div>
            <div className="mt-2">
              <p>Failed :</p>
              <div className="w-full max-w-screen-lg h-40 p-2 text-red-500 bg-gray-100 overflow-auto rounded-lg">
                {failureMessage}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function LockUnLockAccount({ UpdateUserConnect }) {
  const dropdownRef = useRef(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [formData, setFormData] = useState({
    userId: "",
    status: "",
  });
  const [errors, setErrors] = useState({
    userId: "",
    status: "",
  });

  const STATUS_OPTION = [
    { label: "YES", value: "true" },
    { label: "NO", value: "false" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleToggleSearch = () => setIsSearchOpen((prev) => !prev);

  const handleSelectSearch = (option) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      status: option.label, // Display the label in the input
    }));
    setErrors((prevErrors) => ({ ...prevErrors, status: "" }));

    // Ensure dropdown closes
    setTimeout(() => setIsSearchOpen(false), 100);
  };

  const validateForm = () => {
    let isValid = true;
    let newErrors = { userId: "", status: "" };

    if (!formData.userId.trim()) {
      newErrors.userId = "User ID is required.";
      isValid = false;
    }

    if (!formData.status.trim()) {
      newErrors.status = "Status is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        userGuid: formData.userId,
        isAccountLocked: formData.status === "YES" ? true : false,
      };
      UpdateUserConnect(payload)
        .then((response) => {
          if (!!response) {
            toast.success("Update Lock/Unlock account successfully!!");
          }
        })
        .catch((err) => {
          toast.error("Lock/Unlock account failed!! ");
        });
      // Perform submit logic here
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <h5 className="text-2xl font-semibold mb-4">Lock/UnLock User Account</h5>

      {/* User ID Input */}
      <div className="relative w-full max-w-xs mb-4">
        <AppTextField
          name="userId"
          onChange={handleInputChange}
          value={formData.userId}
          placeholder="Enter User ID"
          className="mb-1"
        />
        {errors.userId && (
          <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
        )}
      </div>

      {/* Status Dropdown */}
      <div
        className="relative w-full max-w-xs mb-4"
        ref={dropdownRef}
        onClick={handleToggleSearch}
      >
        <input
          id="status"
          name="status"
          value={formData.status} // Display the selected label
          placeholder="Select status"
          className="px-3 py-[10px] block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm cursor-pointer"
          readOnly // Make input behave like a dropdown
          aria-expanded={isSearchOpen}
        />
        <span className="absolute right-3 top-[1.5rem] transform -translate-y-1/2">
          <img src={ArrowIcon} alt="Arrow Icon" className="w-4 h-4" />
        </span>
        {isSearchOpen && (
          <div className="absolute mt-1 rounded-lg bg-white shadow-lg z-10 w-full">
            <ul
              className="rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 max-h-40 overflow-auto focus:outline-none sm:text-sm"
              role="listbox"
            >
              {STATUS_OPTION.map((option) => (
                <li
                  key={option.value}
                  className="text-gray-900 cursor-pointer select-none py-2 px-4 hover:bg-gray-100 hover:text-black"
                  onClick={() => handleSelectSearch(option)}
                  role="option"
                  aria-selected={formData.status === option.label}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        {errors.status && (
          <p className="text-red-500 text-sm mt-1">{errors.status}</p>
        )}
      </div>

      {/* Submit Button */}
      <div className="relative w-full max-w-xs mt-2">
        <AppButton
          type="submit"
          className="bg-[#FE6634] hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-white w-full py-2 rounded-md"
        >
          Submit
        </AppButton>
      </div>
    </form>
  );
}
